import React, { useState } from 'react'
import UserAvatar from '../shared/UserAvatar'
import ReviewModal from './ReviewModal'

const MILESTONE_MESSAGES = {
  first: {
    'zh-TW': '第一堂課結束了，幫老師寫個評價吧 🎉',
    en: 'First lesson done — leave a review for your teacher 🎉'
  },
  fifth: {
    'zh-TW': '已經上了 5 堂課，幫老師寫個評價吧',
    en: "5 lessons in — leave a review for your teacher"
  },
  final: {
    'zh-TW': '課程結束了，幫老師留個評價吧',
    en: 'Course wrapped up — leave a review for your teacher'
  }
}

export default function ReviewPromptCard({ teachers: initialTeachers, translations = {}, highlight = false, milestoneType = null, showTeamName = true }) {
  const [teachers, setTeachers] = useState(initialTeachers)
  const [selectedTeacher, setSelectedTeacher] = useState(null)
  const [submittedAny, setSubmittedAny] = useState(false)

  const handleSubmitted = (submittedTeacher, reviewData) => {
    setTeachers(prev => prev.filter(t => t.teacherProfileId !== submittedTeacher.teacherProfileId))
    setSelectedTeacher(null)
    setSubmittedAny(true)

    if (reviewData) {
      document.dispatchEvent(new CustomEvent('review-submitted', {
        detail: {
          id: reviewData.id,
          teacherName: submittedTeacher.teacherName,
          teacherAvatarUrl: submittedTeacher.teacherAvatarUrl,
          teacherProfileId: submittedTeacher.teacherProfileId,
          membershipId: submittedTeacher.membershipId,
          teachingTeamId: submittedTeacher.teachingTeamId,
          rating: reviewData.rating,
          tags: reviewData.tags || [],
          comment: reviewData.comment,
          isAnonymous: reviewData.is_anonymous,
          createdAt: reviewData.created_at
        }
      }))
    }
  }

  const t = translations

  // Nothing left to review. If the student just submitted, say so rather than
  // letting the card disappear: the dashboard has a "my reviews" section that
  // catches the review-submitted event, the team page has nothing, so there the
  // vanishing card was the only feedback a submission got.
  //
  // The copy claims only what is true. No notification is sent to the teacher on
  // review creation, and a team needs three public reviews before the page shows
  // any, so neither can be promised here.
  if (teachers.length === 0) {
    if (!submittedAny) return null

    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-5">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="mr-2">🙌</span>
          {t.submitted_title || 'Got it — thanks'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t.submitted_detail || 'You can see your reviews on your dashboard'}{' '}
          <a
            // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
            href="/student/dashboard"
            className="text-[#3366FF] hover:underline font-medium rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3366FF]"
          >
            {t.submitted_link || 'Go to dashboard'}
          </a>
        </p>
      </div>
    )
  }

  const locale = document.documentElement.lang || 'zh-TW'
  const milestoneMsg = highlight && milestoneType && MILESTONE_MESSAGES[milestoneType]
    ? MILESTONE_MESSAGES[milestoneType][locale] || MILESTONE_MESSAGES[milestoneType]['zh-TW']
    : null
  // Highlight with no milestone type is the team page's low-balance case. Its
  // copy comes from i18n through the translations prop -- MILESTONE_MESSAGES
  // above predates that rule and is left alone.
  const lowBalanceMsg = highlight && !milestoneType ? t.low_balance_encouragement : null
  // On a team's own page the team name is already the page, so repeating it on
  // every row says nothing -- and for a teacher known only from a group class it
  // would be the entire subtitle.
  const subtitleFor = (teacher) =>
    [ showTeamName ? teacher.teamName : null, teacher.courseName ].filter(Boolean).join(' · ')

  // The emphasis is a tinted ground, not a heavy outline. On the team page this
  // card sits right under the next-lesson bar, and "you have a lesson in 2 days"
  // has to stay the louder of the two.
  const cardClass = highlight
    ? 'bg-[#E6F0FF] rounded-lg shadow-md p-4 sm:p-5 border border-[#3366FF]/30'
    : 'bg-white rounded-lg shadow-md p-4 sm:p-5'

  return (
    <>
      <div className={cardClass}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="mr-2">⭐</span>
            {t.title || 'Review Your Teacher'}
          </h2>
          <span className="text-sm text-gray-500">
            {(t.teachers_available || '%{count} teacher(s) to review').replace('%{count}', teachers.length)}
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          {milestoneMsg || lowBalanceMsg || t.encouragement || 'Just a few words — it means a lot to your teacher'}
        </p>

        <div className="space-y-2">
          {teachers.map((teacher) => (
            <div
              key={`${teacher.teacherProfileId}-${teacher.teachingTeamId}`}
              className={`flex items-center justify-between p-2.5 rounded-lg ${highlight ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <UserAvatar
                  src={teacher.teacherAvatarUrl}
                  name={teacher.teacherName}
                  size="sm"
                />
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">{teacher.teacherName}</div>
                  {subtitleFor(teacher) && (
                    <div className="text-xs text-gray-500 line-clamp-2">{subtitleFor(teacher)}</div>
                  )}
                </div>
              </div>
              {teacher.existingReviewId ? (
                <a
                  href={`/student/teacher_reviews/${teacher.existingReviewId}/edit`}
                  className="shrink-0 whitespace-nowrap inline-flex items-center min-h-11 sm:min-h-0 px-4 py-1.5 text-sm font-medium text-[#3366FF] border border-[#3366FF] rounded-lg hover:bg-[#E6F0FF] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3366FF]"
                >
                  {t.update_review || 'Update Review'}
                </a>
              ) : (
                <button
                  onClick={() => setSelectedTeacher(teacher)}
                  className="shrink-0 whitespace-nowrap min-h-11 sm:min-h-0 px-4 py-1.5 text-sm font-medium text-white bg-[#3366FF] rounded-lg hover:bg-[#2952CC] transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3366FF] focus-visible:ring-offset-1"
                >
                  {t.review_now || 'Rate Now'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedTeacher && (
        <ReviewModal
          teacher={selectedTeacher}
          onClose={() => setSelectedTeacher(null)}
          onSubmitted={handleSubmitted}
          translations={{ form: t.form, tags: t.tags }}
        />
      )}
    </>
  )
}
